import { lazy, type ComponentType } from "react";
import { Navigate, type RouteObject } from "react-router-dom";
// ── shell + 入口页保持 eager（首屏立即需要·不进按需 chunk）──
import { Layout } from "@/chrome/Layout";
import { AuthGuard } from "@/auth/AuthGuard";
import { LoginPage } from "@/auth/LoginPage";
import { SSOPage } from "@/auth/SSOPage";
import { MfaEnrollPage } from "@/auth/MfaEnrollPage";
import { ForgotPasswordPage } from "@/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "@/auth/ResetPasswordPage";
import { HomePage } from "@/home/HomePage";
import { PlaceholderPage } from "@/pages/PlaceholderPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { NAV_ITEMS } from "@/chrome/navConfig";
import { CustomerReviewPage } from "@/pages/public/CustomerReviewPage"; // 公开页(小·无 Suspense 边界·保 eager)

// ── C1 路由级 code-split：业务页 → 各自 chunk（首屏只下当前页·拆 1.6MB 单体）──
// named export → lazy default 包装。Suspense 边界在 Layout 的 <Outlet> 外。
// loader 用 any（模块含非组件导出如 helper）·组件 props 松化为 any（route 里 props 是字面量·低风险）
const lz = (loader: () => Promise<Record<string, unknown>>, name: string) =>
  lazy(() => loader().then((m) => ({ default: m[name] as ComponentType<any> })));

// 酒店
const HotelsList = lz(() => import("@/pages/hotels/HotelsList"), "HotelsList");
const HotelDetail = lz(() => import("@/pages/hotels/HotelDetail"), "HotelDetail");
const HotelWizard = lz(() => import("@/pages/hotels/HotelWizard"), "HotelWizard");
const HotelRoomForm = lz(() => import("@/pages/hotels/HotelRoomForm"), "HotelRoomForm");
const HotelPolicies = lz(() => import("@/pages/hotels/HotelPolicies"), "HotelPolicies");
const HotelInventoryCalendar = lz(() => import("@/pages/hotels/HotelInventoryCalendar"), "HotelInventoryCalendar");
const HotelRoomInventory = lz(() => import("@/pages/hotels/HotelRoomInventory"), "HotelRoomInventory");
const HotelPolicyWizard = lz(() => import("@/pages/hotels/HotelPolicyWizard"), "HotelPolicyWizard");
const HotelPolicyDetail = lz(() => import("@/pages/hotels/HotelPolicyDetail"), "HotelPolicyDetail");
// 车辆
const VehiclesList = lz(() => import("@/pages/vehicles/VehiclesList"), "VehiclesList");
const VehicleDetail = lz(() => import("@/pages/vehicles/VehicleDetail"), "VehicleDetail");
const VehicleWizard = lz(() => import("@/pages/vehicles/VehicleWizard"), "VehicleWizard");
const VehicleSeatCalendar = lz(() => import("@/pages/vehicles/VehicleSeatCalendar"), "VehicleSeatCalendar");
const VehicleSeatCalendarAll = lz(() => import("@/pages/vehicles/VehicleSeatCalendarAll"), "VehicleSeatCalendarAll");
const RosterPage = lz(() => import("@/pages/vehicles/RosterPage"), "RosterPage");
const VehicleLicenses = lz(() => import("@/pages/vehicles/VehicleLicenses"), "VehicleLicenses");
const VehicleInventoryGuard = lz(() => import("@/pages/vehicles/VehicleInventoryGuard"), "VehicleInventoryGuard");
const VehicleSeatLayout = lz(() => import("@/pages/vehicles/VehicleSeatLayout"), "VehicleSeatLayout");
const VehiclePricing = lz(() => import("@/pages/vehicles/VehiclePricing"), "VehiclePricing");
const VehiclePricingWizard = lz(() => import("@/pages/vehicles/VehiclePricingWizard"), "VehiclePricingWizard");
// 供应商
const SuppliersList = lz(() => import("@/pages/suppliers/SuppliersList"), "SuppliersList");
const SupplierDetail = lz(() => import("@/pages/suppliers/SupplierDetail"), "SupplierDetail");
const SupplierWizard = lz(() => import("@/pages/suppliers/SupplierWizard"), "SupplierWizard");
// 司导
const DriversList = lz(() => import("@/pages/drivers/DriversList"), "DriversList");
const DriverDetail = lz(() => import("@/pages/drivers/DriverDetail"), "DriverDetail");
const DriverWizard = lz(() => import("@/pages/drivers/DriverWizard"), "DriverWizard");
const DriverScheduleCalendar = lz(() => import("@/pages/drivers/DriverScheduleCalendar"), "DriverScheduleCalendar");
const DriverQrTokenManager = lz(() => import("@/pages/drivers/DriverQrTokenManager"), "DriverQrTokenManager");
// 门票
const AttractionsList = lz(() => import("@/pages/attractions/AttractionsList"), "AttractionsList");
const AttractionDetail = lz(() => import("@/pages/attractions/AttractionDetail"), "AttractionDetail");
const AttractionWizard = lz(() => import("@/pages/attractions/AttractionWizard"), "AttractionWizard");
const AttractionInventoryCalendar = lz(() => import("@/pages/attractions/AttractionInventoryCalendar"), "AttractionInventoryCalendar");
const TicketTypeWizard = lz(() => import("@/pages/attractions/TicketTypeWizard"), "TicketTypeWizard");
const TicketInventoryWizard = lz(() => import("@/pages/attractions/TicketInventoryWizard"), "TicketInventoryWizard");
const TicketPricePolicyWizard = lz(() => import("@/pages/attractions/TicketPricePolicyWizard"), "TicketPricePolicyWizard");
// 产品
const ProductsList = lz(() => import("@/pages/products/ProductsList"), "ProductsList");
const ProductDetail = lz(() => import("@/pages/products/ProductDetail"), "ProductDetail");
const ProductWizard = lz(() => import("@/pages/products/ProductWizard"), "ProductWizard");
const TicketProductWizard = lz(() => import("@/pages/products/TicketProductWizard"), "TicketProductWizard");   // WANLE-1
const ProductScheduleCalendar = lz(() => import("@/pages/products/ProductScheduleCalendar"), "ProductScheduleCalendar");
const ProductInventoryCalendar = lz(() => import("@/pages/products/ProductInventoryCalendar"), "ProductInventoryCalendar");
const ProductTapeChart = lz(() => import("@/pages/products/ProductTapeChart"), "ProductTapeChart");
const ListingJobsPage = lz(() => import("@/pages/products/ListingJobsPage"), "ListingJobsPage");
const ListingJobDetailPage = lz(() => import("@/pages/products/ListingJobDetailPage"), "ListingJobDetailPage");
const ProductOptionEditPage = lz(() => import("@/pages/products/ProductOptionEditPage"), "ProductOptionEditPage");   // OPT-C1
const RouteConfigPage = lz(() => import("@/pages/products/RouteConfigPage"), "RouteConfigPage");
// 调度
const DispatchCenter = lz(() => import("@/pages/dispatch/DispatchCenter"), "DispatchCenter");
const DriverSheetPage = lz(() => import("@/pages/dispatch/DriverSheetPage"), "DriverSheetPage");
const DispatchBoard = lz(() => import("@/pages/dispatch/DispatchBoard"), "DispatchBoard");
const DispatchWizard = lz(() => import("@/pages/dispatch/DispatchWizard"), "DispatchWizard");
const DispatchOrderDetail = lz(() => import("@/pages/dispatch/DispatchOrderDetail"), "DispatchOrderDetail");
const DriverScheduleMatrix = lz(() => import("@/pages/dispatch/DriverScheduleMatrix"), "DriverScheduleMatrix");
// 运营
const OpHandoverWall = lz(() => import("@/pages/ops/OpHandoverWall"), "OpHandoverWall");
const ComplaintsPage = lz(() => import("@/pages/ops/ComplaintsPage"), "ComplaintsPage");
const OrdersCenter = lz(() => import("@/pages/ops/SupervisorView"), "OrdersCenter");
// 订单
const OrderDetail = lz(() => import("@/pages/orders/OrderDetailRoute"), "OrderDetailRoute");
const OrderWizard = lz(() => import("@/pages/orders/ManualOrderWizard"), "ManualOrderWizard");   // MANUAL-ORD-1 · 手工订单改版（旧 OrderWizard.tsx 文件保留未删，不再挂路由）
const HotelOrderCalendar = lz(() => import("@/pages/orders/HotelOrderCalendar"), "HotelOrderCalendar");
const CtripWanleRoster = lz(() => import("@/pages/orders/CtripWanleRoster"), "CtripWanleRoster");   // WANLE-2
const TicketOrderCalendar = lz(() => import("@/pages/orders/TicketOrderCalendar"), "TicketOrderCalendar");
const OrderTimeline = lz(() => import("@/pages/orders/OrderTimeline"), "OrderTimeline");
const OrderVoucherView = lz(() => import("@/pages/orders/OrderVoucherView"), "OrderVoucherView");
const OrderConfirmationView = lz(() => import("@/pages/orders/OrderConfirmationView"), "OrderConfirmationView");
const OrderFinancials = lz(() => import("@/pages/orders/OrderFinancials"), "OrderFinancials");
const OrderCancelDialog = lz(() => import("@/pages/orders/OrderCancelDialog"), "OrderCancelDialog");
const OrderCheckoutWizard = lz(() => import("@/pages/orders/OrderCheckoutWizard"), "OrderCheckoutWizard");
// 客户 CRM（ORD3·合并入 C1 lazy 拆分）
const CustomersList = lz(() => import("@/pages/customers/CustomersList"), "CustomersList");
const CustomerDetail = lz(() => import("@/pages/customers/CustomerDetail"), "CustomerDetail");

/** T-S1-A 已实施的真页路由 · 这些 path 会优先 placeholder */
const HOTEL_REAL_ROUTES: Record<string, () => JSX.Element> = {
  "hotels": () => <HotelsList />,
  "hotels/new": () => <HotelWizard />,
  "hotels/:id": () => <HotelDetail />,
  "hotels/:id/edit": () => <HotelWizard />,
  "hotels/:id/inventory": () => <HotelInventoryCalendar />,
  // W3 vehicles
  "vehicles": () => <VehiclesList />,
  "vehicles/new": () => <VehicleWizard />,
  "vehicles/:id": () => <VehicleDetail />,
  "vehicles/:id/edit": () => <VehicleWizard mode="edit" />,
  "vehicles/:id/pricing": () => <VehiclePricing />,
  // W4 suppliers
  "suppliers": () => <SuppliersList />,
  "suppliers/new": () => <SupplierWizard />,
  "suppliers/:id": () => <SupplierDetail />,
  "suppliers/:id/edit": () => <SupplierWizard mode="edit" />,
  // W5 drivers
  "drivers": () => <DriversList />,
  "drivers/new": () => <DriverWizard />,
  "drivers/:id": () => <DriverDetail />,
  "drivers/:id/edit": () => <DriverWizard />,
  // W6 attractions
  "attractions": () => <AttractionsList />,
  "attractions/new": () => <AttractionWizard />,
  "attractions/:id": () => <AttractionDetail />,
  "attractions/:id/edit": () => <AttractionWizard />,
  "attractions/:id/inventory": () => <AttractionInventoryCalendar />,
  // W7 products
  "products": () => <ProductsList />,
  "products/new": () => <ProductWizard />,
  "products/:id": () => <ProductDetail />,
  "products/:id/edit": () => <ProductWizard mode="edit" />,
  // W8+W9 orders · 订单中心按角色渲染（主管/经理/管理员 → 主管视图 page-45 合成全部订单）
  "orders": () => <OrdersCenter />,
  "orders/new": () => <OrderWizard />,
  "orders/:id": () => <OrderDetail />,
  // ORD3 · 客户管理 list + detail
  "customers": () => <CustomersList />,
  "customers/:id": () => <CustomerDetail />,
  // 调度中心（子路由 dispatch/orders/* 在 HOTEL_EXTRA_ROUTES，非标准 :id 模式不能放这里）
  "dispatch": () => <DispatchCenter />,
  // OP 交接中心（nav to=/ops/handoff · 单页无子路由）
  "ops/handoff": () => <OpHandoverWall />,
};

/** W2 + W3 子页 · 不在 NAV_ITEMS 衍生路径内 · 单独追加 */
const HOTEL_EXTRA_ROUTES = [
  // hotels/:id/edit 已在 HOTEL_REAL_ROUTES（经 placeholderModules elem 装配）
  { path: "hotels/:id/rooms/new", element: <HotelRoomForm mode="new" /> },
  { path: "hotels/:id/rooms/:rtid/inventory", element: <HotelRoomInventory /> },
  { path: "hotels/:id/rooms/:rtid/policy/new", element: <HotelPolicyWizard /> },
  { path: "hotels/:id/rooms/:rtid", element: <HotelRoomForm mode="edit" /> },
  { path: "hotels/:id/policies", element: <HotelPolicies /> },
  { path: "hotels/policies/:policyId", element: <HotelPolicyDetail /> },
  { path: "hotels/order-calendar", element: <HotelOrderCalendar /> },
  // W3 vehicles 子页
  { path: "vehicles/seat-calendar", element: <VehicleSeatCalendarAll /> },
  // TAOTUAN-4 · 班表（静态段，排在 vehicles/:id/* 之前语义）
  { path: "vehicles/roster", element: <RosterPage /> },
  { path: "vehicles/:id/seat-calendar", element: <VehicleSeatCalendar /> },
  { path: "vehicles/:id/licenses", element: <VehicleLicenses /> },
  { path: "vehicles/:id/inventory-guard", element: <VehicleInventoryGuard /> },
  // vehicles/:id/edit 已在 HOTEL_REAL_ROUTES（经 placeholderModules elem 装配 · 防占位遮蔽）
  // R-03-B-2 · 配置座位布局向导 page-12（替换占位）
  { path: "vehicles/:id/seat-layout", element: <VehicleSeatLayout /> },
  // R-03-B-4 · 车辆价格 policy page-14（仅 self_contract）
  { path: "vehicles/:id/pricing", element: <VehiclePricing /> },
  { path: "vehicles/:id/pricing/new", element: <VehiclePricingWizard /> },
  { path: "vehicles/:id/pricing/:pid/edit", element: <VehiclePricingWizard /> },
  // W5 drivers 子页
  { path: "drivers/:id/schedule", element: <DriverScheduleCalendar /> },
  { path: "drivers/:id/qr-tokens", element: <DriverQrTokenManager /> },
  // W6 attractions 子页（:id/edit + :id/inventory 已在 HOTEL_REAL_ROUTES 防占位遮蔽）
  // R-05 门票 · 票种向导+编辑 / 票种库存(取消引擎) / 价格 policy 向导
  { path: "attractions/:id/ticket-types/new", element: <TicketTypeWizard /> },
  { path: "attractions/:id/ticket-types/:tid/edit", element: <TicketTypeWizard /> },
  { path: "attractions/:id/ticket-types/:tid/inventory", element: <TicketInventoryWizard /> },
  { path: "attractions/:id/price-policies/new", element: <TicketPricePolicyWizard /> },
  { path: "attractions/order-calendar", element: <TicketOrderCalendar /> },
  // W7 products 子页
  { path: "products/new/ticket", element: <TicketProductWizard /> },   // WANLE-1 · 新建门票产品（静态段，同 tape-chart 排在 :id 之前语义）
  { path: "products/tape-chart", element: <ProductTapeChart /> },  // TAPECHART 运营总表（静态段·排在 :id 之前语义·RR6 静态优先）
  // LIST-3a · 上架任务（静态段，同 tape-chart 排在 :id 之前语义·RR6 静态优先）。
  // listing_agent 开关关着时页面自己判空/404，不在路由层做开关拦截。
  { path: "products/listing-jobs", element: <ListingJobsPage /> },
  { path: "products/listing-jobs/:id", element: <ListingJobDetailPage /> },
  // TAOTUAN-4 · 路线配置（主管；静态段排在 :id 之前）。无权限页面自己判（沿用现有页面做法）。
  { path: "products/routes", element: <RouteConfigPage /> },
  { path: "products/:id/options/:optionId", element: <ProductOptionEditPage /> },   // OPT-C1 · 编辑选项（独立页）
  { path: "products/:id/schedule-calendar", element: <ProductScheduleCalendar /> },
  { path: "products/:id/inventory-calendar", element: <ProductInventoryCalendar /> },
  // W8+W9 orders 子页
  { path: "orders/:id/timeline", element: <OrderTimeline /> },
  { path: "orders/:id/voucher", element: <OrderVoucherView /> },
  { path: "orders/:id/confirmation", element: <OrderConfirmationView /> },
  { path: "orders/:id/financials", element: <OrderFinancials /> },
  { path: "orders/:id/cancel", element: <OrderCancelDialog /> },
  { path: "orders/checkout", element: <OrderCheckoutWizard /> },
  { path: "orders/ctrip-wanle-roster", element: <CtripWanleRoster /> },   // WANLE-2 · 携程玩乐每日名单（静态段）
  // 调度中心 detail flow（深层子路由 · 非标准 :id 模式）
  // BATCH-2 · 派单向导路由从 :orderId 路径参数改为 order_ids 查询参数（N=1..N 统一入口）
  { path: "dispatch/board", element: <DispatchBoard /> },
  { path: "dispatch/assign", element: <DispatchWizard /> },
  { path: "dispatch/orders/:id", element: <DispatchOrderDetail /> },
  { path: "dispatch/driver-schedule", element: <DriverScheduleMatrix /> },
];

// T-S1-C window C pages
import { ChannelPartnersListPage } from "@/pages/b2b/ChannelPartnersListPage";
import { ChannelPartnerWizardPage } from "@/pages/b2b/ChannelPartnerWizardPage";
import { ChannelPartnerDetailPage } from "@/pages/b2b/ChannelPartnerDetailPage";
import { AccountManagerWorkbenchPage } from "@/pages/b2b/AccountManagerWorkbenchPage";
import { SettlementCenterPage } from "@/pages/b2b/SettlementCenterPage";
import { CreditApprovalQueuePage } from "@/pages/b2b/CreditApprovalQueuePage";
import { OpenApiApprovalsPage } from "@/pages/b2b/OpenApiApprovalsPage";

import { PricingDashboardPage } from "@/pages/pricing/PricingDashboardPage";
import { ChannelMarkupConfigPage } from "@/pages/pricing/ChannelMarkupConfigPage";
import { B2BOverridesPage } from "@/pages/pricing/B2BOverridesPage";
import { PricingChangeLogPage } from "@/pages/pricing/PricingChangeLogPage";
// W4
import { PricingCenterPage } from "@/pages/pricing/PricingCenterPage";
// 售价管理中心 · 成本引擎 5 page
import { ChannelConfigPage } from "@/pages/pricing/ChannelConfigPage";
import { PricingProductDetailPage } from "@/pages/pricing/PricingProductDetailPage";
import { CustomerPricesPage } from "@/pages/pricing/CustomerPricesPage";   // PRICE-CUST-1
import { SellPricingSettingsPage } from "@/pages/pricing/SellPricingSettingsPage";
import { TierConfigPage } from "@/pages/b2b/TierConfigPage";
import { ChannelPricingDetailPage } from "@/pages/pricing/ChannelPricingDetailPage";
import { CustomerOverridesPage } from "@/pages/pricing/CustomerOverridesPage";
import { HolidayPricingPage } from "@/pages/pricing/HolidayPricingPage";

// W5 · FinanceOverviewPage 替换 W1 FinanceDashboardPage
import { FinanceOverviewPage } from "@/pages/finance/FinanceOverviewPage";
import { FinanceDashboardPage } from "@/pages/finance/FinanceDashboardPage";
import { ReceivablesPage } from "@/pages/finance/ReceivablesPage";
import { PayablesPage } from "@/pages/finance/PayablesPage";
import { InvoicesPage } from "@/pages/finance/InvoicesPage";
import { NewInvoicePage } from "@/pages/finance/NewInvoicePage";
import { ReportsPage } from "@/pages/finance/ReportsPage";
import { FinanceExpensesPage } from "@/pages/finance/FinanceExpensesPage";

// CHANCTR · 渠道中心接真——旧 5 tab（平台管理/同步日志/错误重试/抓取作业/数据映射）
// 组件已删（数据映射撤·抓取作业剥离独立项目·平台管理+同步日志+错误重试接真为新 2 tab）。
// QBIntegrationPage 不在范围内（财务模块用，路由/后端 mock 均原样保留）。
import { QBIntegrationPage } from "@/pages/integration/QBIntegrationPage";
import { ChannelCenterPage } from "@/pages/channelCenter/ChannelCenterPage";
import { ListingCenterPage } from "@/pages/listing/ListingCenterPage";
import { CtripLookupsPage } from "@/pages/listing/CtripLookupsPage";   // CTRIP-P1
import { PlatformDetailPage } from "@/pages/channelCenter/PlatformDetailPage";
import { GlobalSyncLogPage } from "@/pages/channelCenter/GlobalSyncLogPage";

import { AnalyticsDashboardPage } from "@/pages/analytics/AnalyticsDashboardPage";
import { VehicleOpsOverview } from "@/pages/vehicle-ops/VehicleOpsOverview";
import { VehicleOpsMaintenance } from "@/pages/vehicle-ops/VehicleOpsMaintenance";
import { VehicleOpsMaintenanceForm } from "@/pages/vehicle-ops/VehicleOpsMaintenanceForm";
import { VehicleOpsExpenses } from "@/pages/vehicle-ops/VehicleOpsExpenses";
import { VehicleOpsExpenseForm } from "@/pages/vehicle-ops/VehicleOpsExpenseForm";
import { VehicleOpsLicenseForm } from "@/pages/vehicle-ops/VehicleOpsLicenseForm";
import { DeepAnalysisPage } from "@/pages/analytics/DeepAnalysisPage";
import { ChannelUsagePivotPage } from "@/pages/analytics/ChannelUsagePivotPage";

import { UsersPage } from "@/pages/rbac/UsersPage";
import { RolesPage } from "@/pages/rbac/RolesPage";
import { RolesPermissionsPage } from "@/pages/rbac/RolesPermissionsPage";
import { PermissionsPage } from "@/pages/rbac/PermissionsPage";
import { AuditLogsPage } from "@/pages/rbac/AuditLogsPage";
import { RecycleBinPage } from "@/pages/rbac/RecycleBinPage";

import { GeneralSettingsPage } from "@/pages/settings/GeneralSettingsPage";
import { NotificationsSettingsPage } from "@/pages/settings/NotificationsSettingsPage";
import { ConfirmationTemplateSettingsPage } from "@/pages/settings/ConfirmationTemplateSettingsPage";
import { CancellationPolicySettingsPage } from "@/pages/settings/CancellationPolicySettingsPage";
import { AiSettingsPage } from "@/pages/settings/AiSettingsPage";
import { PlacesSettingsPage } from "@/pages/settings/PlacesSettingsPage";
import { SiteServiceSettingsPage } from "@/pages/settings/SiteServiceSettingsPage";   // PAY-1 · 客服与收款
import { SitePaymentsPage } from "@/pages/sitePayments/SitePaymentsPage";   // PAY-1 · 官网收款   // IMGLIB-1 · 地点清单
import { MediaLibraryPage } from "@/pages/media/MediaLibraryPage";           // IMGLIB-1 · 图片库
// T-S2-C-1 · 演示工具
import { DemoToolsPage } from "@/pages/system/DemoToolsPage";

// T-S2-A-1 · 数据导入工具
import { ImportToolPage } from "@/pages/import/ImportToolPage";
// T-S2-B-4 · 系统告警中心
import { AlertsPage } from "@/pages/system/AlertsPage";

/**
 * 路由表
 *  - T-FE-01 Sprint 0: 18 一级模块占位（PlaceholderPage）
 *  - T-S1-C: 替换 b2b / pricing / finance / integration / analytics / rbac(permissions) / settings 为实页
 *  - 其它模块（hotels / vehicles / drivers / attractions / suppliers / products / orders /
 *    handoff / dispatch / vehicle-operations）仍为占位 · 由 T-S1-A / T-S1-B 工程师填实
 */

// 一级模块占位（保持其它窗口的模块）
const placeholderModules = NAV_ITEMS.filter(
  (n) =>
    n.to !== "/" &&
    !n.sub &&   // PAY-1 · 侧栏子项（官网收款）有自己的实页路由
    ![
      "channel-partners",
      "pricing",
      "listing",
      "media-library",
      "finance",
      "api-integration",
      "analytics",
      "permissions",
      "settings",
      "import-tool",
    ].includes(n.id),
).flatMap((n) => {
  const path = n.to.replace(/^\//, "");
  const elem = (p: string, fallbackTitle: string) =>
    HOTEL_REAL_ROUTES[p]
      ? HOTEL_REAL_ROUTES[p]()
      : <PlaceholderPage title={fallbackTitle} />;
  const list: RouteObject[] = [
    { path, element: elem(path, n.labelCn) },
    { path: `${path}/new`, element: elem(`${path}/new`, `${n.labelCn} · 新建`) },
    { path: `${path}/:id`, element: elem(`${path}/:id`, `${n.labelCn} · 详情`) },
    { path: `${path}/:id/edit`, element: elem(`${path}/:id/edit`, `${n.labelCn} · 编辑`) },
  ];
  if (["hotels", "vehicles", "attractions", "products"].includes(n.id)) {
    list.push(
      { path: `${path}/:id/inventory`, element: elem(`${path}/:id/inventory`, `${n.labelCn} · 库存`) },
      { path: `${path}/:id/pricing`, element: elem(`${path}/:id/pricing`, `${n.labelCn} · 定价`) },
    );
  }
  if (["products", "vehicles"].includes(n.id)) {
    list.push({ path: `${path}/:id/schedule`, element: <PlaceholderPage title={`${n.labelCn} · 排程`} /> });
  }
  return list;
});

const windowCRoutes: RouteObject[] = [
  // QR1 客户投诉处理（非 NAV 项·经 home todo /ops/complaints?status=new 进入）
  { path: "ops/complaints", element: <ComplaintsPage /> },
  // B2B
  { path: "admin/channel-partners", element: <ChannelPartnersListPage /> },
  { path: "admin/channel-partners/new", element: <ChannelPartnerWizardPage /> },
  { path: "admin/channel-partners/approvals", element: <CreditApprovalQueuePage /> },
  { path: "admin/channel-partners/open-api", element: <OpenApiApprovalsPage /> },
  { path: "admin/channel-partners/workbench", element: <AccountManagerWorkbenchPage /> },
  { path: "admin/channel-partners/settlements", element: <SettlementCenterPage /> },
  { path: "admin/channel-partners/:id/edit", element: <ChannelPartnerWizardPage /> },
  { path: "admin/channel-partners/:id", element: <ChannelPartnerDetailPage /> },

  // 售价管理中心 · 成本引擎 5 page（① 售价总览 / ② 产品售价详情 / ③ 渠道配置 / ④ 同行价目表 / ⑤ 售价配置）
  { path: "admin/pricing", element: <PricingCenterPage /> },
  { path: "admin/pricing/product/:id", element: <PricingProductDetailPage /> },
  { path: "admin/pricing/channels", element: <ChannelConfigPage /> },
  { path: "admin/pricing/price-book", element: <Navigate to="/admin/pricing" replace /> },   // PRICEUNIFY-1 · 同行价目表取消：旧链接落到售价总览（同行 Tab）
  { path: "admin/pricing/customer-prices", element: <CustomerPricesPage /> },
  { path: "admin/pricing/sell-config", element: <SellPricingSettingsPage /> },
  { path: "admin/pricing/tiers", element: <TierConfigPage /> },
  { path: "admin/pricing/dashboard-legacy", element: <PricingDashboardPage /> },
  { path: "admin/pricing/channel-markups", element: <ChannelMarkupConfigPage /> },
  { path: "admin/pricing/channels/:channel", element: <ChannelPricingDetailPage /> },
  { path: "admin/pricing/customer-overrides", element: <CustomerOverridesPage /> },
  { path: "admin/pricing/holidays", element: <HolidayPricingPage /> },
  { path: "admin/pricing/overrides", element: <B2BOverridesPage /> },
  { path: "admin/pricing/change-requests", element: <PricingChangeLogPage /> },

  // Finance · W5 升级主页为 FinanceOverviewPage (4 stat tile + QB badge + recent invoices)
  { path: "admin/finance", element: <FinanceOverviewPage /> },
  { path: "admin/finance/dashboard-legacy", element: <FinanceDashboardPage /> },
  { path: "admin/finance/receivables", element: <ReceivablesPage /> },
  { path: "admin/finance/payables", element: <PayablesPage /> },
  { path: "admin/finance/invoices", element: <InvoicesPage /> },
  { path: "admin/finance/invoices/new", element: <NewInvoicePage /> },
  { path: "admin/finance/reports", element: <ReportsPage /> },
  { path: "admin/finance/expenses", element: <FinanceExpensesPage /> },

  // 渠道中心（CHANCTR）· 数据映射（已撤·业务映射并入上架中心）/抓取作业（剥离独立项目）
  // 旧路由不再注册；同步日志/错误重试并入全局日志视图 E，旧路径指向新页面（避免旧收藏链接死掉）。
  { path: "admin/listing", element: <ListingCenterPage /> },
  { path: "admin/listing/ctrip-lookups", element: <CtripLookupsPage /> },   // CTRIP-P1 · 携程对照表
  { path: "admin/api-integration", element: <ChannelCenterPage /> },
  { path: "admin/api-integration/sync-logs", element: <GlobalSyncLogPage /> },
  { path: "admin/api-integration/error-queue", element: <GlobalSyncLogPage /> },
  { path: "admin/api-integration/qb", element: <QBIntegrationPage /> },
  { path: "admin/api-integration/:code", element: <PlatformDetailPage /> },

  // Analytics
  { path: "ops/analytics", element: <AnalyticsDashboardPage /> },
  { path: "ops/analytics/deep", element: <DeepAnalysisPage /> },
  { path: "ops/analytics/channel-usage", element: <ChannelUsagePivotPage /> },

  // RBAC (permissions module)
  { path: "admin/permissions", element: <UsersPage /> },
  // canvas 4 子页 Tab：用户管理 / 角色管理(RolesPage) / 权限矩阵(RolesPermissionsPage) / 审计日志
  { path: "admin/permissions/roles", element: <RolesPage /> },
  { path: "admin/permissions/matrix", element: <RolesPermissionsPage /> },
  { path: "admin/permissions/permissions", element: <PermissionsPage /> },
  { path: "admin/permissions/audit-logs", element: <AuditLogsPage /> },
  { path: "admin/permissions/trash", element: <RecycleBinPage /> }, // A6 回收站（tech_admin only）

  // Settings
  { path: "admin/settings", element: <GeneralSettingsPage /> },
  { path: "admin/settings/notifications", element: <NotificationsSettingsPage /> },
  { path: "admin/settings/confirmation-template", element: <ConfirmationTemplateSettingsPage /> },
  { path: "admin/settings/cancellation-policy", element: <CancellationPolicySettingsPage /> },
  { path: "admin/settings/ai", element: <AiSettingsPage /> },
  { path: "admin/settings/site-service", element: <SiteServiceSettingsPage /> },
  { path: "admin/site-payments", element: <SitePaymentsPage /> },
  { path: "admin/settings/places", element: <PlacesSettingsPage /> },
  // IMGLIB-1 · 图片库（侧栏「资源类 → 图片库」）
  { path: "media-library", element: <MediaLibraryPage /> },

  // T-S2-A-1 · 数据导入工具
  { path: "admin/system/import", element: <ImportToolPage /> },
  // T-S2-C-1 · 演示工具 (tech_admin only · nav 已过滤)
  { path: "admin/system/demo-tools", element: <DemoToolsPage /> },
  // T-S2-B-4 · 系统告警中心
  { path: "admin/system/alerts", element: <AlertsPage /> },
  // 车务台账（canvas page-64~69 · 逐页接入）
  { path: "admin/vehicle-operations", element: <VehicleOpsOverview /> },
  { path: "admin/vehicle-operations/maintenance", element: <VehicleOpsMaintenance /> },
  { path: "admin/vehicle-operations/maintenance/new", element: <VehicleOpsMaintenanceForm /> },
  { path: "admin/vehicle-operations/expenses", element: <VehicleOpsExpenses /> },
  { path: "admin/vehicle-operations/expenses/upload", element: <VehicleOpsExpenseForm /> },
  { path: "admin/vehicle-operations/expenses/license", element: <VehicleOpsLicenseForm /> },
];

export const routes: RouteObject[] = [
  { path: "/login", element: <LoginPage /> },
  { path: "/sso", element: <SSOPage /> },
  // MFAFIX-1 · 登录死锁分支跳转目标——不在 AuthGuard 内（此时还没有正常 access
  // token，只有一枚 10 分钟受限令牌，靠 router state 传，见 LoginPage.tsx）。
  { path: "/mfa-enroll", element: <MfaEnrollPage /> },
  // PWRESET-1 · 忘记密码：申请页 + 邮件链接落地页（?token=）。都是未登录页，不进 AuthGuard。
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  // QR1 凭证 QR 客户评分/投诉页（公开·无 AuthGuard·无 Layout·扫码开·移动端）
  { path: "/review/:token", element: <CustomerReviewPage /> },
  // TAOTUAN-5 · 司导名单打印页：要登录（带 Bearer 取 JSON），但不要侧栏 / 顶栏（打印干净）
  { path: "/dispatch/runs/:id/driver-sheet", element: <AuthGuard><DriverSheetPage /></AuthGuard> },
  {
    path: "/",
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <HomePage /> },
      // T-S1-C 实页 (b2b / pricing / finance / integration / analytics / permissions / settings)
      ...windowCRoutes,
      // T-S1-A 实页 (hotels / vehicles / suppliers) + 其它 placeholder
      ...placeholderModules,
      // T-S1-A hotel/vehicle 额外子页 (rooms/policies/seat-calendar/licenses/inventory-guard)
      ...HOTEL_EXTRA_ROUTES,
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];
